import React, { useState } from 'react';

const ROLES = [
  'FrontDesk & Billing',
  'Director & Corporate',
  'Clinical Team',
  'Credentialing Specialist',
  'Office Team Lead',
  'Regional Manager',
];

const INITIAL = { fullName: '', phone: '', email: '', officeLocation: '', role: '' };

function validate(fields) {
  const errors = {};
  if (!fields.fullName.trim()) errors.fullName = 'Full name is required.';
  else if (fields.fullName.trim().length < 2) errors.fullName = 'Please enter your full name.';
  if (!fields.phone.trim()) errors.phone = 'Phone number is required.';
  else if (!/^[\d\s\-().+]{7,20}$/.test(fields.phone.trim())) errors.phone = 'Please enter a valid phone number.';
  if (!fields.email.trim()) errors.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!fields.officeLocation.trim()) errors.officeLocation = 'Office location is required.';
  if (!fields.role) errors.role = 'Please select your job role.';
  return errors;
}

export default function IntakeForm({ onSubmit }) {
  const [fields, setFields]   = useState(INITIAL);
  const [errors, setErrors]   = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
    if (touched[name]) {
      const errs = validate({ ...fields, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: errs[name] }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const errs = validate(fields);
    setErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const allTouched = Object.keys(fields).reduce((a, k) => ({ ...a, [k]: true }), {});
    setTouched(allTouched);
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    setTimeout(() => {
      onSubmit({
        fullName:       fields.fullName.trim().replace(/\b\w/g, c => c.toUpperCase()),
        phone:          fields.phone.trim(),
        email:          fields.email.trim().toLowerCase(),
        officeLocation: fields.officeLocation.trim(),
        role:           fields.role,
      });
    }, 400);
  };

  const fieldErr = (name) =>
    touched[name] && errors[name] ? <span className="form-error">⚠ {errors[name]}</span> : null;

  return (
    <div className="page-container">
      <div style={{ marginBottom: 28 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 1 of 4</div>
        <h1 className="page-title">Employee Onboarding</h1>
        <p className="page-subtitle">
          Please complete the form below to begin your HIPAA Compliance Training.
          All fields are required before you can access the training modules.
        </p>
      </div>

      <div className="card">
        <div className="info-box">
          <p>
            <strong>Krona Dentistry / Odyssey Solutions</strong> requires all staff to
            complete annual HIPAA Compliance Training. Your information will be used
            solely to generate your completion certificate and log your training record.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-grid">
            <div className="form-group form-group--full">
              <label className="form-label" htmlFor="fullName">Full Name <span>*</span></label>
              <input id="fullName" name="fullName" type="text"
                className={`form-input${touched.fullName && errors.fullName ? ' error' : ''}`}
                placeholder="e.g. Jane Smith" value={fields.fullName}
                onChange={handleChange} onBlur={handleBlur} autoComplete="name" disabled={submitting} />
              {fieldErr('fullName')}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone Number <span>*</span></label>
              <input id="phone" name="phone" type="tel"
                className={`form-input${touched.phone && errors.phone ? ' error' : ''}`}
                placeholder="e.g. (555) 867-5309" value={fields.phone}
                onChange={handleChange} onBlur={handleBlur} autoComplete="tel" disabled={submitting} />
              {fieldErr('phone')}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email Address <span>*</span></label>
              <input id="email" name="email" type="email"
                className={`form-input${touched.email && errors.email ? ' error' : ''}`}
                placeholder="e.g. jane@kronadentistry.com" value={fields.email}
                onChange={handleChange} onBlur={handleBlur} autoComplete="email" disabled={submitting} />
              {fieldErr('email')}
              <span className="form-hint">Your certificate will be emailed to this address.</span>
            </div>

            <div className="form-group form-group--full">
              <label className="form-label" htmlFor="officeLocation">Office Location <span>*</span></label>
              <input id="officeLocation" name="officeLocation" type="text"
                className={`form-input${touched.officeLocation && errors.officeLocation ? ' error' : ''}`}
                placeholder="e.g. Krona Dentistry Main Office" value={fields.officeLocation}
                onChange={handleChange} onBlur={handleBlur} disabled={submitting} />
              {fieldErr('officeLocation')}
              <span className="form-hint">Enter the name of your office or clinic location.</span>
            </div>

            <hr className="form-divider" />

            <div className="form-group form-group--full">
              <label className="form-label" htmlFor="role">Job-Specific Role <span>*</span></label>
              <div className="select-wrapper">
                <select id="role" name="role"
                  className={`form-select${touched.role && errors.role ? ' error' : ''}`}
                  value={fields.role} onChange={handleChange} onBlur={handleBlur} disabled={submitting}>
                  <option value="">— Select your role —</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {fieldErr('role')}
              <span className="form-hint">Your final training video will be tailored to this role.</span>
            </div>
          </div>

          <div style={{ marginTop: 32 }}>
            <button type="submit" className="btn btn-primary btn--full btn--lg" disabled={submitting}>
              {submitting ? (
                <><span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Starting Training…</>
              ) : 'Begin HIPAA Training →'}
            </button>
          </div>
        </form>
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[{ icon: '🔒', text: 'Secure & Confidential' }, { icon: '📋', text: 'HIPAA Compliant Training' }, { icon: '🏆', text: 'Certificate Upon Completion' }].map(({ icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#546E7A' }}>
            <span style={{ fontSize: 18 }}>{icon}</span>{text}
          </div>
        ))}
      </div>
    </div>
  );
}
